import {Component, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {
  DraftConfirmationDialogComponent
} from "../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component";
import {DownloadDialogComponent} from "../dialogs/lab-manager/download-dialog/download-dialog.component";
import {ResultsProblemsDialogComponent} from "../dialogs/lab-manager/results-problems-dialog/results-problems-dialog.component";
import {ResultsUploadDialogComponent, ResultsUploadDialogResult} from "../dialogs/lab-manager/results-upload-dialog/results-upload-dialog.component";
import {LabUploadResult} from "./models/lab-upload-result";
import {classifyUpload, orderedProblems, summariseUpload} from "./utils/lab-upload-response";
import {LabManagerSharedDataService} from "./lab-manager-shared-data.service";
import {LabManagerService} from "./lab-manager.service";
import {BasicLabManagerItemModel} from "../models/lab-manager/basic-lab-manager-item";
import {LabSubmissionDetailsModel} from "../models/lab-manager/lab-submission-details";
import {NavBarComponent} from "../nav-bar/nav-bar.component";
import {AgronimistModel} from "../models/lab-manager/agronimist";
import {CustomerModel} from "../order-to-cash/models-otc/customer-model";
import {LabCostItemModel} from "../models/lab-manager/lab-cost-item";
import { HttpResponse } from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {User} from "../models/user";

@Component({
    selector: 'app-lab-manager',
    templateUrl: './lab-manager.component.html',
    styleUrls: ['./lab-manager.component.css'],
    standalone: false
})
export class LabManagerComponent implements OnInit, OnDestroy{

  labSubmissionDetails: LabSubmissionDetailsModel = null;
  submissionId: number;
  currentUser: User = JSON.parse(sessionStorage.getItem('user'));
  hasSavedClient: boolean = false;
  updatingChanges: boolean = true;
  labSubmissionDetailsValid: boolean = true;
  labHasSamples: boolean = false;
  labList: BasicLabManagerItemModel[];
  agronomistList: AgronimistModel[];
  isApprover: boolean = false;
  resultsAreCorrect: boolean = false;
  customerList: CustomerModel[];
  isLoadingCustomers: boolean = true;
  selectedCustomer: CustomerModel;
  analysisCostItems: LabCostItemModel[] = [];
  saveCount: number = 0;
  submissionTypes: BasicLabManagerItemModel[] = [];
  hasOneAnalysis: boolean = false;
  hasAllAnalysis: boolean = false;
  hasOneSample: boolean = false;
  statusList: BasicLabManagerItemModel[] = []

  constructor(
      public router: Router,
      private route: ActivatedRoute,
      private dialog: MatDialog,
      private labManagerSharedDataService: LabManagerSharedDataService,
      private labManagerService: LabManagerService,
      private nav: NavBarComponent,
      public snackBar: MatSnackBar,
  ) {
    /*
     * takeUntilDestroyed, and it is the fix for samples vanishing mid-submission.
     *
     * This subscription used to outlive the component. The comment below said the
     * shell "is never destroyed", which is true while you stay inside Lab Manager
     * and false the moment you go to another module: the route deactivates, the
     * component is destroyed, and coming back builds a new one. The old handler
     * was still subscribed.
     *
     * A dead instance still receives every NavigationEnd in the app, and the
     * router has detached its route, so this.route.firstChild is null and routeId
     * comes back undefined. It therefore took the no-id branch below and cleared
     * the submission on a user who was working inside one. Subscribers fire in
     * subscription order, so the dead handlers ran first and the live one, which
     * only refetches when the id CHANGES, never repaired it. One trip out of Lab
     * Manager and back was enough; each further trip added another handler.
     *
     * Reported 16/09/2026 as samples disappearing on the samples, analysis and
     * results tabs. One branch, all three tabs: samples reads submissionId as an
     * input, and the other two read labSubmissionDetailsData$.
     */
    router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if(event instanceof NavigationEnd) {
        // Which submission is on screen, taken from the active child route.
        //
        // This component is the shell for the module, the submissions list
        // included, so submissionId is sticky across a tab switch. That is
        // what made the previous approach fail. It matched the URL string for
        // /lab-manager/submissions, loaded the header on a setTimeout, and only
        // did so when there was no current id.
        //
        // The id guard was the visible bug: going from one submission straight to
        // another never reloaded, so the header kept the previous submission while
        // the tab below it loaded the right one. The timer raced the router, and a
        // firstChild that had not settled gave an undefined id. And the reset
        // depended on the list route being spelled exactly that way.
        //
        // Reading the child route covers all three. NavigationEnd fires after
        // activation, so firstChild is the route now on screen.
        const routeId = this.route.firstChild?.snapshot.params['id'];

        if(routeId == null) {
          // No id on the route means the submissions list.
          this.submissionId = null;
          this.hasSavedClient = false;
          this.labSubmissionDetails = null;
          this.analysisCostItems = [];
          this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
          // Clear the shared subject too. It is a BehaviorSubject, so anything
          // subscribing later gets whatever was last pushed into it, and leaving a
          // submission without clearing leaves that submission on offer.
          this.labManagerSharedDataService.updateLabSubmissionDetails(null);
        } else if(Number(routeId) !== this.submissionId) {
          // Number, because route params are strings and this field is declared a
          // number. It was previously assigned the raw string.
          //
          // Only when the id actually changes: moving between the tabs of one
          // submission must not refetch, which is what the old guard achieved by
          // accident and this does on purpose.
          this.submissionId = Number(routeId);
          this.getLabSubmissionDetails();
        } else if (
          this.labSubmissionDetails &&
          this.labManagerSharedDataService.getLabSubmissionDetails() == null
        ) {
          /*
           * Same submission, but the shared value has been cleared underneath us.
           *
           * Restored rather than refetched. The tabs read the submission from a
           * BehaviorSubject, so a null does not merely skip an update: it is
           * replayed to every tab opened afterwards, and this branch is the one
           * path a same-submission navigation takes, so nothing else would ever
           * put the value back. Cheap, and it makes a stray clear self-healing
           * instead of permanent until the user reloads.
           */
          this.labManagerSharedDataService.updateLabSubmissionDetails(this.labSubmissionDetails);
        }
      }
    });

    // The subscription that used to live here has been removed rather than fixed.
    //
    // It read labSubmissionDetailsData$ and copied every field of the emitted
    // object onto this.labSubmissionDetails. Both writers of that subject are in
    // this component, and both push the very object this.labSubmissionDetails
    // already points at, so it only ever copied the field onto itself.
    //
    // It could still fail. The subject is a BehaviorSubject that keeps its last
    // value for the life of the app, so on re-entering Lab Manager from another
    // module it replayed the previous submission the moment this constructor
    // subscribed, while labSubmissionDetails was still null. Assigning into null
    // throws, which killed the subscription and reported an error for a copy that
    // achieved nothing in the first place.
  }

  ngOnInit(): void {
    this.getSubmissionTypes();
    this.getLabSubmissionStatusList();
  }

  getLabSubmissionStatusList() {
    this.labManagerService.getLabManagerStatusList().subscribe({
      next: (res: BasicLabManagerItemModel[]) => {
        this.statusList = res;
      }
    });
  }

  /**
   * The label for a status id.
   *
   * By id, not by array position. The template did statusList[id - 1], which is
   * only right while the endpoint returns the statuses sorted by id and numbered
   * contiguously from 1. Nothing guarantees either, and if it ever stops being
   * true every label shifts by one and the badge quietly lies.
   */
  statusDescription(statusId: number): string {
    if(!statusId) {
      return '-';
    }

    return this.statusList?.find(s => s.id === statusId)?.description ?? '-';
  }

  getSubmissionTypes() {
    this.labManagerService.getSubmissionTypes().subscribe((res: BasicLabManagerItemModel[]) => {
      this.submissionTypes = res;
    });
  }

  openBackDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'back',
        dialogTitle: 'GO BACK?',
        dialogSubTitle: 'Unsaved changes will be lost, are you sure?'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if(res) {
        this.submissionId = null;
        this.hasSavedClient = false;
        sessionStorage.removeItem('currentAgent');
        this.labSubmissionDetails = null;
        this.analysisCostItems = [];
        this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
        this.router.navigateByUrl('lab-manager');
      }
    })
  }

  getLabSubmissionDetails() {
    this.getLabList();
    this.getCustomerDetails();
    this.getAgronomistList();
    this.checkLabSubmissionSampleValidity();
    this.nav.toggleLoading(true);
    this.labManagerService.getLabSubmissionDetails(this.submissionId).subscribe({
      next: (res: LabSubmissionDetailsModel) => {
        this.labSubmissionDetails = res;
        this.isApprover = this.labSubmissionDetails?.agronomistUserId === this.currentUser.id;
        this.labManagerSharedDataService.updateApprover(this.isApprover);
        this.setSelectedCustomer();
        this.checkSubmissionHasOneAnalysis();
        this.retrieveLabAnalysisItems(false, this.labSubmissionDetails);

        this.hasSavedClient = !!this.labSubmissionDetails?.otcCustomerDetailId;

        this.labManagerSharedDataService.updateLabSubmissionDetails(this.labSubmissionDetails);
        this.updatingChanges = false;
        this.nav.toggleLoading(false);
      },
      error: () => {
        this.nav.toggleLoading(false);
      }})
  }

  getLabList() {
    this.labManagerService.getLabList().subscribe({
      next: (res) => {
        this.labList = res;
      }
    });
  }

  checkSubmissionHasOneAnalysis() {
    if(this.labSubmissionDetails?.id) {
      this.labManagerService.checkLabSubmissionAnalysisValidity(this.labSubmissionDetails.id).subscribe({
        next: (res: any) => {
          this.hasOneAnalysis = res?.hasOneAnalysis;
          this.hasAllAnalysis = res?.hasAllAnalysis;
        }
      })
    }
  }

  // checkSubmissionHasOneAnalysis() {
  //   this.labManagerService.checkSubmissionHasOneAnalysis(this.labSubmissionDetails.id).subscribe({
  //     next: (res: boolean) => {
  //       this.sampleHasAnalysis = res;
  //     }
  //   })
  // }

  checkLabSubmissionSampleValidity() {
    this.labManagerService.checkLabSubmissionSampleValidity(this.submissionId).subscribe({
      next: (res: boolean) => {
        this.hasOneSample = res;
      },
      error: () => {
        this.hasOneSample = false;
      }
    })
  }

  getCustomerDetails() {
    this.isLoadingCustomers = true;
    // Fast: get just the customer & payer for this submission instantly
    if (this.submissionId) {
      this.labManagerService.getSubmissionCustomers(this.submissionId).subscribe({
        next: (res) => {
          // Only use fast result if full list hasn't arrived yet
          if (this.isLoadingCustomers) {
            const initial = [];
            if (res.customer) initial.push(res.customer);
            if (res.payer && res.payer.id !== res.customer?.id) initial.push(res.payer);
            if (initial.length > 0) {
              this.customerList = initial;
              this.setSelectedCustomer();
            }
          }
        }
      });
    }
    // Background: load full customer list for dropdown changes
    this.labManagerService.getCustomers().subscribe({
      next: (res) => {
        this.customerList = res;
        this.isLoadingCustomers = false;
        this.setSelectedCustomer();
      }
    });
  }

  setSelectedCustomer() {
    if(this.customerList?.length > 0 && this.labSubmissionDetails?.otcCustomerDetailId) {
      this.selectedCustomer = this.customerList.find(c => c.id === this.labSubmissionDetails.otcCustomerDetailId);
    }
  }

  onLabSubmissionDetailChanges(event) {
    if(event?.labSubmissionDetails?.otcCustomerDetailId !== this.labSubmissionDetails?.otcCustomerDetailId) {
      this.hasSavedClient = false;
    } else if(this.labSubmissionDetails?.labManLabId !== event?.labSubmissionDetails?.labManLabId) {
      this.hasSavedClient = false;
    }
    this.labSubmissionDetails = event.labSubmissionDetails;
    this.labSubmissionDetailsValid = event.isValid;
  }

  getAgronomistList() {
    this.labManagerService.getAgronomistList().subscribe({
      next: (res) => {
        this.agronomistList = res;
      }
    })
  }

  openRejectDraftDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'reject',
        dialogTitle: 'REJECT RESULTS',
        dialogSubTitle: 'Are you sure you want to reject this lab submission results?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);
        this.saveSubmission('REJECTED')
      }
    })
  }

  openDeleteDraftDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'delete',
        dialogTitle: 'DELETE DRAFT',
        dialogSubTitle: 'Are you sure you want to delete this lab submission?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);
        this.labManagerService.deleteLabSubmission(this.submissionId).subscribe({
          next: () => {
            this.nav.toggleLoading(false);
            this.submissionId = null;
            this.hasSavedClient = false;
            this.labSubmissionDetails = null;
            this.analysisCostItems = [];
            this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
            this.router.navigateByUrl('lab-manager');
          }
        })
      }
    })
  }

  saveLabAnalysisCostItems(tempSubmission: LabSubmissionDetailsModel) {
    if(this.analysisCostItems?.length > 0) {
      for(let costItem of this.analysisCostItems) {
        this.labManagerService.saveAnalysisCostItem(costItem).subscribe({
          next: () => {
            this.saveCount++;
            if(this.saveCount === this.analysisCostItems.length) {
              this.nav.toggleLoading(false);
              this.saveCount = 0;
              this.updateLabSubmissionDetails(tempSubmission);
            }
          },
          error: () => {
            this.saveCount++;
            if (this.saveCount === this.analysisCostItems.length) {
              this.nav.toggleLoading(false);
              this.saveCount = 0;
              this.updateLabSubmissionDetails(tempSubmission);
            }
          }
        });
      }
    } else {
      this.updateLabSubmissionDetails(tempSubmission);
    }
  }

  openApproveResultsDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'approve',
        dialogTitle: 'APPROVE RESULTS',
        dialogSubTitle: 'Please insure that all information is correct before approving lab results.'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.approveResults();
      }
    });
  }

  approveResults() {
    this.nav.toggleLoading(true);
    this.labManagerService.approveLabResults(this.labSubmissionDetails.id).subscribe({
      next: (res) => {
        this.labManagerService.getLabSubmissionDetails(this.submissionId).subscribe({
          next: (res: LabSubmissionDetailsModel) => {
            this.labSubmissionDetails = res;
            this.saveSubmission('RESULTS_APPROVED');
          }});
      },
      error: (error) => {
        this.nav.toggleLoading(false);
      }
    })
  }

  openSaveDraftDialog(submissionType: string) {
    let data = {};

    if(submissionType === 'SAVE') {
      data = {
        dialogType: 'save',
        dialogTitle: 'SAVE DRAFT',
        dialogSubTitle: 'Are you sure you want to save draft?'
      }
    } else {
      data = {
        dialogType: 'submit',
        dialogTitle: 'SUBMIT TO LAB',
        dialogSubTitle: 'Please ensure that all information is correct before submitting to lab.'
      }
    }
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: data
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.saveSubmission(submissionType)
      }
    })
  }

  saveSubmission(submissionType: string) {
    let accountPayer = this.customerList.find(c => c.id === this.labSubmissionDetails.orderAccountId);
    let tempSubmission = this.labSubmissionDetails;
    if(submissionType === 'SAVE') {
      tempSubmission.labManStatusId = 1;
    } else if (submissionType === 'SUBMIT'){
      if(accountPayer?.sGrp === "ZAF" && (!this.labSubmissionDetails.coopNo || this.labSubmissionDetails?.coopNo?.trim().length === 0)) {
        tempSubmission.labManStatusId = 4;
      }  else {
        tempSubmission.labManStatusId = 2;
      }
    } else if(submissionType === 'RESULTS_UPLOADED') {
      tempSubmission.labManStatusId = 5;
    } else if(submissionType === 'AGRONOMIST') {
      tempSubmission.labManStatusId = 7;
    } else if(submissionType === 'REJECTED') {
      tempSubmission.labManStatusId = 8;
    } else {
      tempSubmission.labManStatusId = 6;
    }
    this.isApprover = this.labSubmissionDetails?.agronomistUserId === this.currentUser.id;
    this.labManagerSharedDataService.updateApprover(this.isApprover);

    if(tempSubmission.labManStatusId < 5) {
      if(this.analysisCostItems.length > 0 && this.router.url.includes('cost-calculator')) {
        this.saveLabAnalysisCostItems(tempSubmission);
      } else {
        this.retrieveLabAnalysisItems(true, tempSubmission);
      }
    } else {
      this.updateLabSubmissionDetails(tempSubmission);
    }

  }

  retrieveLabAnalysisItems(shouldSave: boolean, tempSubmission: LabSubmissionDetailsModel) {
    if(this.labSubmissionDetails?.labManLabId && this.labSubmissionDetails?.labManTypesId) {
      this.labManagerService.getAnalysisItems(this.labSubmissionDetails.labManLabId, this.labSubmissionDetails.labManTypesId).subscribe({
        next: (labManItems: BasicLabManagerItemModel[]) => {
          this.labManagerService.getLabSubmissionCalculator(this.labSubmissionDetails.id).subscribe({
            next: (res) => {
              this.analysisCostItems = [];

              for(let item of labManItems) {
                let index = res.findIndex(r => r.labManItemsId === item.id);

                if(index !== -1) {
                  this.analysisCostItems.push({...res[index],
                    description: item.description
                  });
                }
              }
              this.labManagerSharedDataService.updateLabAnalysisCostItems(this.analysisCostItems);
              if(shouldSave) {
                this.saveLabAnalysisCostItems(tempSubmission);
              }

              this.nav.toggleLoading(false);
            },
            error: () => {
              this.nav.toggleLoading(false);
            }
          })
        },
        error: () => {
          this.nav.toggleLoading(false);
        }
      });
    } else if(shouldSave) {
      this.updateLabSubmissionDetails(tempSubmission);
    }
  }

  updateLabSubmissionDetails(tempSubmission: LabSubmissionDetailsModel) {
    this.nav.toggleLoading(true);
    this.labManagerService.updateLabSubmissionDetail(tempSubmission, this.submissionId).subscribe({
      next: () => {
        this.labSubmissionDetails = tempSubmission;
        if(this.labSubmissionDetails.otcCustomerDetailId) {
          this.hasSavedClient = true;
        }
        this.nav.toggleLoading(false);
        this.labManagerSharedDataService.updateLabSubmissionDetails(this.labSubmissionDetails);
      },
      error: () => {
        this.nav.toggleLoading(false);
      }
    })
  }

  /**
   * Choose a results file, upload it, and see what was wrong with it, in one dialog.
   *
   * The dialog owns the upload and the retry loop, so a refused file does not send
   * the user back here to press UPLOAD again. This method only reacts to what it
   * closes with, which is the one thing the dialog deliberately does not do: move
   * the submission on.
   */
  openUploadDialog() {
    const dialogRef = this.dialog.open(ResultsUploadDialogComponent, {
      // 760 rather than the 520 it started at. A problem reads
      // 'Sample "100-2-Block A1" is on this submission but has no row in this file.',
      // which wrapped onto two lines at the old width, so halving the wrapping roughly
      // doubles how many fit before the list scrolls. That compounds with the 60vh.
      //
      // The width lives here alone. The inner div carries max-w only, never a fixed
      // width: setting both double-counted against the panel's own padding and put a
      // horizontal scrollbar on the dialog.
      width: '760px',
      // Default maxWidth is 80vw, which would claw this back to 720px on a 900px
      // screen. Explicit so a narrow screen gives the list MORE room rather than less.
      maxWidth: '92vw',
      // The problem list inside grows to 60vh before it scrolls. Without a ceiling on
      // the panel itself the heading and the buttons get pushed off the screen, which
      // is worse than scrolling because the user cannot reach Close.
      maxHeight: '90vh',
      disableClose: true,
      data: {
        submissionId: this.labSubmissionDetails.id,
        labId: this.labSubmissionDetails.labManLabId,
        typesId: this.labSubmissionDetails.labManTypesId,
        isSoftTissue: this.labSubmissionDetails.labManTypesId === 1,
      }
    });

    dialogRef.afterClosed().subscribe((outcome: ResultsUploadDialogResult | null) => {
      // Nothing stored: a refused file, or the user backed out. The submission has
      // not progressed and must not say it has.
      if (!outcome?.stored) {
        return;
      }

      if (outcome.verdict === 'ok') {
        this.snackBar.open("Successfully Uploaded File", 'close', { duration: 5000 });
      } else {
        // Never the hard-coded success line over a response nobody read.
        this.snackBar.open(summariseUpload(outcome.result, outcome.verdict), 'close', { duration: 8000 });
      }

      this.nav.toggleLoading(true);
      this.saveSubmission("RESULTS_UPLOADED");
    });
  }

  /**
   * READY FOR AGRONOMIST, checked before it happens.
   *
   * The button stays enabled on status alone. A greyed-out button explains nothing, and
   * the whole request here was to be told what is wrong so it can be fixed, so the
   * explanation has to be reachable.
   *
   * The check runs against what is stored rather than against the last upload, so it is
   * still right if the user has since added or removed a sample.
   */
  readyForAgronomist() {
    this.nav.toggleLoading(true);

    this.labManagerService.validateResults(this.labSubmissionDetails.id).subscribe({
      next: (result) => {
        this.nav.toggleLoading(false);
        this.showAgronomistCheck(result);
      },
      error: () => {
        // Fail open, visibly. Hard-blocking on a network error would strand someone
        // with no way forward and nothing to act on, which is worse than the gap it
        // would be closing.
        this.nav.toggleLoading(false);
        this.confirmReadyForAgronomist(
          'The results could not be checked just now. Send them to the agronomist anyway?');
      }
    });
  }

  /**
   * What to show once the results have been checked.
   *
   * Separate from the call that fetches them so the decision can be read on its own,
   * and so a caller that already has a result does not have to go back to the network.
   */
  private showAgronomistCheck(result: LabUploadResult | null) {
    const verdict = classifyUpload(result);

    if(verdict === 'ok' || verdict === 'unknown') {
      this.confirmReadyForAgronomist('Send these results to the agronomist?');
      return;
    }

    this.dialog.open(ResultsProblemsDialogComponent, {
      // Matched to the upload dialog above on purpose. The two render the same problem
      // list for the same submission, and a user who meets both in one sitting should
      // not see the same sentence wrap differently in each.
      width: '760px',
      maxWidth: '92vw',
      maxHeight: '90vh',
      data: {
        verdict,
        dialogTitle: verdict === 'blocking'
          ? 'These results cannot go to the agronomist yet'
          : 'Send these results to the agronomist?',
        summary: verdict === 'blocking'
          ? 'Fix the problems below and upload the results file again.'
          : summariseUpload(result, verdict),
        problems: orderedProblems(result),
        problemsOmitted: result?.problemsOmitted ?? 0,
        confirmText: 'Send anyway'
      }
    }).afterClosed().subscribe(proceed => {
      if(proceed) {
        this.saveSubmission('AGRONOMIST');
      }
    });
  }

  /** A plain confirm. READY was the only transition here with none at all. */
  private confirmReadyForAgronomist(message: string) {
    this.dialog.open(DraftConfirmationDialogComponent, {
      width: '400px',
      data: {
        dialogType: 'confirm',
        dialogTitle: 'Ready for agronomist',
        dialogSubTitle: message,
        confirmText: 'Send'
      }
    }).afterClosed().subscribe(confirmed => {
      if(confirmed) {
        this.saveSubmission('AGRONOMIST');
      }
    });
  }

  downloadLabReport() {
    this.nav.toggleLoading(true);
    this.labManagerService.downloadLabSubmissionExcel(this.submissionId, 2).subscribe({
      next: (resp: HttpResponse<Blob>) => {
        let data = resp.body


        if (data) {
          const url = window.URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = url;

          link.setAttribute('download', String(resp.headers.get('content-disposition')));

          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        }
        this.snackBar.open("Download Successful", "close", {
          duration: 5000,
        });
        this.nav.toggleLoading(false);
      },
      error: () => {
        this.nav.toggleLoading(false);
        this.snackBar.open("Error Downloading Lab Report", "close", {
          duration: 5000,
        });
      }
    });
    // const dialogRef = this.dialog.open(DownloadDialogComponent, {
    //   width: '420px'
    // });

    // dialogRef.afterClosed().subscribe(res => {
    //   if(res) {
        
    //   }
    // })
  }

  ngOnDestroy(): void {
    this.submissionId = null;
    this.hasSavedClient = false;
    sessionStorage.removeItem('currentAgent');
    this.labSubmissionDetails = null;
    this.analysisCostItems = [];
    this.labManagerSharedDataService.updateLabAnalysisCostItems([]);
  }
}
